import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listSeoDailyReports,
  runSeoDailyNow,
  setSeoKeywordStatus,
  saveSeoDailyNotes,
  type SeoDailyKeyword,
  type SeoKeywordStatus,
} from "@/lib/seo-daily.functions";

const fmtInt = new Intl.NumberFormat("sl-SI", { maximumFractionDigits: 0 }).format;
const fmtDate = (d: string) => new Date(d).toLocaleDateString("sl-SI", { day: "2-digit", month: "2-digit", year: "numeric" });

const STATUS_LABELS: Record<SeoKeywordStatus, { label: string; cls: string }> = {
  predlagano: { label: "Predlagano", cls: "bg-slate-100 text-slate-800" },
  v_delu: { label: "V delu", cls: "bg-amber-100 text-amber-900" },
  izvedeno: { label: "Izvedeno", cls: "bg-green-100 text-green-800" },
  zavrnjeno: { label: "Zavrnjeno", cls: "bg-red-100 text-red-800" },
};

function Stars({ n }: { n: number }) {
  return <span className="tabular-nums text-accent" aria-label={`Potencial ${n} od 5`}>{"★".repeat(n)}<span className="text-muted-foreground">{"★".repeat(Math.max(0, 5 - n))}</span></span>;
}

export function SeoDailySection() {
  const list = useServerFn(listSeoDailyReports);
  const run = useServerFn(runSeoDailyNow);
  const setStatus = useServerFn(setSeoKeywordStatus);
  const saveNotes = useServerFn(saveSeoDailyNotes);
  const qc = useQueryClient();

  const [openId, setOpenId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);

  const { data: reports = [], isLoading, error } = useQuery({
    queryKey: ["seo-daily-reports"],
    queryFn: () => list(),
  });

  const runMut = useMutation({
    mutationFn: () => run(),
    onSuccess: (res) => {
      setMsg(res.ok ? `Analiza končana — ${fmtInt(res.count ?? 0)} ključnih besed.` : `Napaka: ${res.error ?? "neznana"}`);
      qc.invalidateQueries({ queryKey: ["seo-daily-reports"] });
      setTimeout(() => setMsg(null), 6000);
    },
    onError: (e: Error) => {
      setMsg(`Napaka: ${e.message}`);
      setTimeout(() => setMsg(null), 8000);
    },
  });

  const statusMut = useMutation({
    mutationFn: (v: { reportId: string; keyword: string; status: SeoKeywordStatus }) => setStatus({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["seo-daily-reports"] }),
  });

  const notesMut = useMutation({
    mutationFn: (v: { reportId: string; notes: string }) => saveNotes({ data: v }),
    onSuccess: () => {
      setMsg("Zapiski shranjeni.");
      qc.invalidateQueries({ queryKey: ["seo-daily-reports"] });
      setTimeout(() => setMsg(null), 4000);
    },
  });

  const renderKeywords = (reportId: string, keywords: SeoDailyKeyword[], type: "short" | "long") => {
    const rows = keywords.filter((k) => k.type === type);
    if (rows.length === 0) return <p className="text-sm text-muted-foreground">Ni predlogov v tej skupini.</p>;
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="py-2 pr-3">Ključna beseda</th>
              <th className="py-2 pr-3 text-right">Prikazi</th>
              <th className="py-2 pr-3 text-right">Kliki</th>
              <th className="py-2 pr-3 text-right">CTR</th>
              <th className="py-2 pr-3 text-right">Pozicija</th>
              <th className="py-2 pr-3">Potencial</th>
              <th className="py-2 pr-3">Priporočena akcija</th>
              <th className="py-2 pr-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((k) => (
              <tr key={k.keyword} className="align-top">
                <td className="py-3 pr-3">
                  <div className="font-medium">{k.keyword}</div>
                  <div className="text-xs text-muted-foreground mt-1 max-w-md">{k.reason}</div>
                </td>
                <td className="py-3 pr-3 text-right tabular-nums">{fmtInt(k.impressions)}</td>
                <td className="py-3 pr-3 text-right tabular-nums">{fmtInt(k.clicks)}</td>
                <td className="py-3 pr-3 text-right tabular-nums">{(k.ctr * 100).toFixed(1)} %</td>
                <td className="py-3 pr-3 text-right tabular-nums">{k.position.toFixed(1)}</td>
                <td className="py-3 pr-3"><Stars n={k.potential} /></td>
                <td className="py-3 pr-3">
                  {k.action === "enrich" && k.targetUrl ? (
                    <>
                      <span className="block">Obogati obstoječi članek</span>
                      <a href={k.targetUrl} target="_blank" rel="noreferrer" className="text-brand underline text-xs break-all">
                        {k.targetTitle ?? k.targetUrl}
                      </a>
                    </>
                  ) : (
                    <span>Nov članek na to temo</span>
                  )}
                </td>
                <td className="py-3 pr-3">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-xs mb-1 ${STATUS_LABELS[k.status].cls}`}>
                    {STATUS_LABELS[k.status].label}
                  </span>
                  <select
                    aria-label={`Status za ${k.keyword}`}
                    className="input h-8 text-xs w-full"
                    value={k.status}
                    onChange={(e) => statusMut.mutate({ reportId, keyword: k.keyword, status: e.target.value as SeoKeywordStatus })}
                  >
                    {(Object.keys(STATUS_LABELS) as SeoKeywordStatus[]).map((s) => (
                      <option key={s} value={s}>{STATUS_LABELS[s].label}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl">SEO dnevnik</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Vsako jutro ob 6.00 samodejna analiza Search Console: priložnosti na 2.–3. strani (pozicija 11–30) in poizvedbe
            z visokimi prikazi ter nizkim CTR. Podatki Search Console zaostajajo približno 2 dni. Objava vsebine ostane ročna.
          </p>
        </div>
        <button
          onClick={() => runMut.mutate()}
          disabled={runMut.isPending}
          className="rounded-md bg-brand text-white px-4 py-2 text-sm hover:bg-brand/90 disabled:opacity-60"
        >
          {runMut.isPending ? "Analiziram…" : "Zaženi analizo zdaj"}
        </button>
      </div>

      {msg ? <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">{msg}</div> : null}
      {error ? <div className="text-sm text-destructive">Napaka: {(error as Error).message}</div> : null}

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Nalagam…</div>
      ) : reports.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">
          Ni še poročil. Kliknite »Zaženi analizo zdaj« ali počakajte na jutranji zagon ob 6.00.
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => {
            const open = openId === r.id;
            const done = r.keywords.filter((k) => k.status === "izvedeno").length;
            return (
              <div key={r.id} className="rounded-lg border border-border">
                <button
                  onClick={() => setOpenId(open ? null : r.id)}
                  aria-expanded={open}
                  className="w-full flex flex-wrap items-center justify-between gap-2 p-4 text-left hover:bg-muted/40"
                >
                  <div>
                    <div className="font-medium">Poročilo {fmtDate(r.report_date)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {fmtInt(r.keywords.length)} ključnih besed · {fmtInt(done)} izvedenih
                      {r.range_from && r.range_to ? ` · obdobje ${fmtDate(r.range_from)}–${fmtDate(r.range_to)}` : ""}
                    </div>
                  </div>
                  <span className="text-sm text-muted-foreground">{open ? "Skrij" : "Prikaži"}</span>
                </button>

                {open && (
                  <div className="border-t border-border p-4 space-y-6">
                    {r.summary ? <p className="text-sm">{r.summary}</p> : null}

                    <div className="space-y-2">
                      <h3 className="font-serif text-lg">Kratke ključne besede (short-tail)</h3>
                      {renderKeywords(r.id, r.keywords, "short")}
                    </div>

                    <div className="space-y-2">
                      <h3 className="font-serif text-lg">Dolge besedne zveze (long-tail)</h3>
                      {renderKeywords(r.id, r.keywords, "long")}
                    </div>

                    <div className="space-y-2">
                      <h3 className="font-serif text-lg">Izvedeno ta dan</h3>
                      <textarea
                        aria-label="Zapiski o izvedenih spremembah"
                        className="input min-h-28 w-full"
                        placeholder="Kaj je bilo obogateno ali objavljeno…"
                        value={notesDraft[r.id] ?? r.done_notes ?? ""}
                        onChange={(e) => setNotesDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                      />
                      <div className="flex justify-end">
                        <button
                          onClick={() => notesMut.mutate({ reportId: r.id, notes: notesDraft[r.id] ?? r.done_notes ?? "" })}
                          disabled={notesMut.isPending}
                          className="rounded-md border border-brand text-brand px-4 py-2 text-sm hover:bg-brand/5 disabled:opacity-60"
                        >
                          {notesMut.isPending ? "Shranjujem…" : "Shrani zapiske"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
